import React, { useState } from 'react';
import { AuthLayout, Button, CodeInput, Emphasis, ResendCode } from '@talbenmoshe/trip-os-design-system';
import { SignInActivity, SignInFailure, useSignIn, useSignInFacade } from '@talbenmoshe/trip-os-react-model';
import { useMessages } from '../i18n/messagesContext.js';
import { CodeStepPrimaryButton } from './CodeStepPrimaryButton.js';
import { SignInNoticeBanner } from './SignInNoticeBanner.js';

/** Step 2 of sign-in (frames D1–D8): the 6-digit code, resend, and the expired and locked states. */
export function CodeStep() {
  const messages = useMessages();
  const signIn = useSignIn();
  const facade = useSignInFacade();
  const [codeDraft, setCodeDraft] = useState('');
  let codeError: string | undefined;

  if (signIn.failure === SignInFailure.WRONG_CODE) {
    codeError = signIn.attemptsLeft === 1 ? messages.SIGN_IN_WRONG_CODE_ONE_ATTEMPT_LEFT() : messages.SIGN_IN_WRONG_CODE_ATTEMPTS_LEFT(String(signIn.attemptsLeft));
  }

  function handleResend(): void {
    setCodeDraft('');
    void facade.resendSignInCode();
  }

  function handleVerify(code: string): void {
    void facade.verifySignInCode(code);
  }

  // An unusable code is never shown again, so the draft starts empty for the new code.
  function handleSubmit(): void {
    if (!signIn.isCodeUsable) {
      setCodeDraft('');
    }

    void facade.submitSignInCode(codeDraft);
  }

  return (
    <AuthLayout
      wordmarkLabel={messages.SIGN_IN_WORDMARK_LABEL()}
      title={messages.SIGN_IN_CODE_TITLE()}
      description={messages.SIGN_IN_CODE_DESCRIPTION(signIn.email, <Emphasis>{signIn.email}</Emphasis>)}
      topAction={
        <Button
          label={messages.SIGN_IN_USE_DIFFERENT_EMAIL()}
          variant="tertiary"
          iconStart="arrow-left"
          disabled={signIn.isBusy}
          onClick={() => facade.chooseDifferentEmail()}
        />
      }
      notice={<SignInNoticeBanner failure={signIn.failure} notice={signIn.notice} />}
      onSubmit={handleSubmit}
    >
      <CodeInput
        label={messages.SIGN_IN_CODE_LABEL()}
        value={signIn.isCodeUsable ? codeDraft : ''}
        onChange={setCodeDraft}
        onComplete={handleVerify}
        error={codeError}
        errorRevision={signIn.failureRevision}
        disabled={signIn.isBusy || !signIn.isCodeUsable}
      />
      <CodeStepPrimaryButton />
      {signIn.isCodeUsable && (
        <ResendCode
          secondsLeft={signIn.secondsUntilResend}
          onResend={handleResend}
          sending={signIn.activity === SignInActivity.RESENDING_CODE}
          countdownText={messages.SIGN_IN_RESEND_COUNTDOWN()}
          availableText={messages.SIGN_IN_RESEND_AVAILABLE()}
          resendLabel={messages.SIGN_IN_RESEND_CODE()}
          sendingLabel={messages.SIGN_IN_RESENDING_CODE()}
          availableAnnouncement={messages.SIGN_IN_RESEND_ANNOUNCEMENT()}
        />
      )}
    </AuthLayout>
  );
}
